/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Badge, Box, Flex, HStack, Text, useToken, VStack } from '@chakra-ui/core';
import React, { useMemo } from 'react';
import { MdCheckBox, MdCheckBoxOutlineBlank, MdRadioButtonChecked, MdRadioButtonUnchecked } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import isBoolean from 'lodash/isBoolean';
import { QUESTIONNAIRE_ELEMENT_TYPE } from '../../questionnaireTemplate.enums';
import { DenormalizedElement } from '../../questionnaireTemplate.types';
import { formElementSchema } from '../../formBuilder/schemas/formElement.schema';

const QuestionBody: React.FC<{ question: DenormalizedElement }> = ({ question }) => {
  const [blue500, gray400] = useToken('colors', ['blue.500', 'gray.400']);

  const schema = formElementSchema[question.questionType];

  const { t } = useTranslation('questionnaireTemplate');

  const preferredAnswer = useMemo(() => {
    if (
      question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO ||
      question.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE
    ) {
      if (!isBoolean(question.preferredAnswer)) return undefined;
      const branches = schema?.branches;
      /* istanbul ignore if */
      if (!branches) return undefined;

      // Use translation key instead of raw branch value
      const branchIndex = question.preferredAnswer ? 0 : 1;
      return t(`formBuilder.schema.yesno.branches.${branchIndex}`);
    }

    if (question.options?.length && question.preferredAnswers?.length) {
      const preferredOptionIds = new Set(question.preferredAnswers);
      return question.options
        .filter((o) => preferredOptionIds.has(o.id))
        .map((o) => o.text)
        .join(', ');
    }

    return undefined;
  }, [question, schema, t]);
  const isSingle = question?.totalAnswer && question?.totalAnswer === 1;

  const preferredOptionIds = useMemo(() => new Set(question.preferredAnswers ?? []), [question.preferredAnswers]);

  switch (question.questionType) {
    case QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM:
      return (
        <HStack spacing={3} mt={3}>
          <Text fontSize="sm">{t('modal.maxAnswerLength')}</Text>
          <Badge bg="#EDF2F7" textTransform="capitalize" color="gray.600" px={3} py={1} borderRadius={6}>
            {t('modal.characters', { count: question.maxLength })}
          </Badge>
        </HStack>
      );
    case QUESTIONNAIRE_ELEMENT_TYPE.YESNO:
    case QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE:
      return (
        <HStack spacing={3} mt={3}>
          <Text fontSize="sm">{t('modal.expectedAnswer')}</Text>
          <Badge bg="#EDF2F7" textTransform="capitalize" color="gray.600" px={3} py={1} borderRadius={6}>
            {preferredAnswer}
          </Badge>
        </HStack>
      );

    case QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE:
    case QUESTIONNAIRE_ELEMENT_TYPE.MULTI_CHOICE_WITH_IMAGE:
      if (!question.options?.length) return null;
      return (
        <VStack spacing={3} align="stretch" mt={3}>
          <Text fontSize="sm">{t('modal.answerOptions')}</Text>
          {question.options.map((o, index) => {
            const isActive = preferredOptionIds.has(o.id);
            let Icon;
            if (isSingle) {
              Icon = isActive ? MdRadioButtonChecked : MdRadioButtonUnchecked;
            } else {
              Icon = isActive ? MdCheckBox : MdCheckBoxOutlineBlank;
            }

            const optionLetter = String.fromCharCode(65 + index);

            return (
              <Flex key={o.id} align="center" gap={3}>
                <Icon color={isActive ? blue500 : gray400} size={20} />
                <Text fontSize="14px" color="gray.600" w="20px" ml={2}>
                  {optionLetter}
                </Text>
                <Box flex="1" bg="input.background" borderRadius="md" px={4} py={3}>
                  <Text fontSize="14px" color="gray.800" whiteSpace="pre-line" wordBreak="break-word">
                    {o.text}
                  </Text>
                </Box>
              </Flex>
            );
          })}
        </VStack>
      );

    case QUESTIONNAIRE_ELEMENT_TYPE.DOCUMENT_UPLOAD:
      return (
        <HStack spacing={3} mt={3} align="center">
          <Text fontSize="sm">{t('formBuilder.canvas.element.documentUpload.supportedTypeTitle')}</Text>
          <Box px={3} py={1} borderRadius="md" border="1px solid #AED0FF" bg="#EFF6FF">
            <Text color="blue.500" fontSize="xs">
              {t('formBuilder.canvas.element.documentUpload.supportedTypes')}
            </Text>
          </Box>
        </HStack>
      );

    default:
      return null;
  }
};

export default QuestionBody;
